import React, { useCallback, useState } from 'react';
import { Button, Divider, Typography } from 'antd';
import {
  CheckOutlined,
  CopyOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { resolveSkillIcon } from '../../capabilities/capabilityIcons.jsx';

const { Text } = Typography;
const conditionalValue = (condition, whenTrue, whenFalse) => (
  condition ? whenTrue : whenFalse
);

const writeClipboard = async (text) => {
  const writeText = globalThis.navigator?.clipboard?.writeText;
  if (typeof writeText !== 'function') return false;
  await writeText.call(globalThis.navigator.clipboard, text);
  return true;
};

const writeLegacyClipboard = (text) => {
  if (typeof globalThis.document === 'undefined') return false;
  const textarea = globalThis.document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  globalThis.document.body.appendChild(textarea);
  textarea.select();
  const copied = globalThis.document.execCommand('copy');
  globalThis.document.body.removeChild(textarea);
  return copied;
};

const copyMessageText = async (text) => {
  try {
    if (await writeClipboard(text)) return true;
  } catch {
    // Continue with the browser fallback.
  }
  try {
    return writeLegacyClipboard(text);
  } catch {
    return false;
  }
};

export function buildCapabilityActionButtons({ actions = [], onAction, themeTokens }) {
  if (typeof onAction !== 'function') return [];
  return (Array.isArray(actions) ? actions : []).map((entry) => {
    return (
      <Button
        aria-label={entry.label}
        className="flare-message-capability-action"
        icon={resolveSkillIcon(entry)}
        key={entry.key || entry.actionKey}
        onClick={() => onAction(entry)}
        size="small"
        title={entry.label}
        type="text"
        style={{ color: themeTokens.textSecondary, paddingInline: 4 }}
      />
    );
  });
}

export default function MessageActionRow({
  capabilityActions = [],
  canRegenerate = false,
  copyableContent = '',
  createdAt,
  isUser,
  onCapabilityAction,
  onRegenerate,
  streaming = false,
  themeTokens,
}) {
  const [copySuccess, setCopySuccess] = useState(false);
  const actionStyle = isUser
    ? { color: themeTokens.bubbleUserBg }
    : { color: themeTokens.textSecondary };
  const alignment = conditionalValue(isUser, 'flex-end', 'flex-start');
  const messageRoleClass = conditionalValue(isUser, 'is-user', 'is-assistant');
  const visibleCapabilityActions = !isUser && !streaming
    ? buildCapabilityActionButtons({
      actions: capabilityActions,
      onAction: onCapabilityAction,
      themeTokens,
    })
    : [];

  const copyContent = useCallback(async () => {
    const value = String(copyableContent ?? '');
    if (!value.trim() || !await copyMessageText(value)) return;
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 1200);
  }, [copyableContent]);

  return (
    <div
      className={`flare-message-action-row ${messageRoleClass}`}
      style={{
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        justifyContent: alignment,
        marginTop: 4,
        width: '100%',
      }}
    >
      <Text
        className="flare-message-timestamp"
        type={conditionalValue(isUser, undefined, 'secondary')}
        style={{ ...actionStyle, flex: '0 0 auto', fontSize: 11.5 }}
      >
        {conditionalValue(
          streaming,
          '生成中',
          conditionalValue(Boolean(createdAt), new Date(createdAt).toLocaleTimeString(), ''),
        )}
      </Text>
      <Button
        aria-label={conditionalValue(copySuccess, '已复制', '复制')}
        title={conditionalValue(copySuccess, '已复制', '复制')}
        className="flare-message-copy-button"
        icon={conditionalValue(copySuccess, <CheckOutlined />, <CopyOutlined />)}
        onClick={copyContent}
        size="small"
        type="text"
        style={{ ...actionStyle, paddingInline: 4 }}
      />
      {conditionalValue(canRegenerate, (
        <Button
          icon={<ReloadOutlined />}
          onClick={onRegenerate}
          size="small"
          type="text"
          style={{ color: themeTokens.textSecondary, paddingInline: 4 }}
        >
          重新生成
        </Button>
      ), null)}
      {conditionalValue(visibleCapabilityActions.length > 0, (
        <>
          <Divider orientation="vertical" role="separator" aria-orientation="vertical" />
          <span className="flare-message-capability-group" style={{ display: 'inline-flex', gap: 6 }}>
            {visibleCapabilityActions}
          </span>
        </>
      ), null)}
    </div>
  );
}
